import { apiClient } from './api';
import { User, TeamInfo } from '../types/auth';

export interface LoginRequest {
  userId: string;
  password: string;
}

export interface RegisterRequest {
  userId: string;
  password: string;
  name: string;
}

export interface AuthResponse {
  token: string;
  refreshToken: string;
  user: User;
  team?: TeamInfo;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export class SimpleAuthService {
  // ログイン
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw this.handleAuthError(error);
    }
  }

  // 新規登録
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      throw this.handleAuthError(error);
    }
  }

  // ログアウト
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
      // ログアウトエラーは無視（トークンが無効でも問題ない）
    }
  }

  // トークンリフレッシュ
  async refreshToken(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
    try {
      const response = await apiClient.post('/auth/refresh', { refreshToken });
      return response.data;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw this.handleAuthError(error);
    }
  }

  // パスワード変更
  async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    try {
      await apiClient.put('/auth/password', passwordData);
    } catch (error) {
      console.error('Change password error:', error);
      throw this.handleAuthError(error);
    }
  }

  // ユーザー情報取得
  async getCurrentUser(): Promise<{ user: User; team?: TeamInfo }> {
    try {
      const response = await apiClient.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw this.handleAuthError(error);
    }
  }

  // チーム作成
  async createTeam(teamName: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/teams', { teamName });
      return response.data;
    } catch (error) {
      console.error('Create team error:', error);
      throw this.handleAuthError(error);
    }
  }

  // チーム参加
  async joinTeam(inviteCode: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/teams/join', { inviteCode });
      return response.data;
    } catch (error) {
      console.error('Join team error:', error);
      throw this.handleAuthError(error);
    }
  }

  // エラーハンドリング
  private handleAuthError(error: any): Error {
    if (error?.response?.data?.error) {
      const errorMessage = error.response.data.error;
      
      // 特定のエラーメッセージを日本語に変換
      switch (errorMessage) {
        case 'Invalid credentials':
          return new Error('メールアドレスまたはパスワードが正しくありません');
        case 'User already exists':
          return new Error('このメールアドレスは既に登録されています');
        case 'Invalid email format':
          return new Error('有効なメールアドレスを入力してください');
        case 'Password too weak':
          return new Error('パスワードは大文字・小文字・数字を含む8文字以上で入力してください');
        case 'Current password is incorrect':
          return new Error('現在のパスワードが正しくありません');
        case 'Team not found':
          return new Error('チームが見つかりません');
        case 'Invalid invite code':
          return new Error('招待コードが正しくありません');
        case 'Team name already exists':
          return new Error('このチーム名は既に使用されています');
        case 'Authentication required':
          return new Error('認証が必要です。再度ログインしてください');
        case 'Token expired':
          return new Error('セッションが期限切れです。再度ログインしてください');
        default:
          return new Error(errorMessage);
      }
    }

    if (error?.response?.status === 401) {
      return new Error('認証に失敗しました。再度ログインしてください');
    }

    if (error?.response?.status === 403) {
      return new Error('この操作を実行する権限がありません');
    }

    if (error?.response?.status === 404) {
      return new Error('リソースが見つかりません');
    }

    if (error?.response?.status === 429) {
      return new Error('リクエストが多すぎます。しばらく待ってから再度お試しください');
    }

    if (error?.response?.status >= 500) {
      return new Error('サーバーエラーが発生しました。しばらく待ってから再度お試しください');
    }

    if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
      return new Error('ネットワークエラーが発生しました。インターネット接続を確認してください');
    }

    return new Error('予期しないエラーが発生しました');
  }
}

// シングルトンインスタンス
export const simpleAuthService = new SimpleAuthService();